import { ITeamChatMemberModel, ITeamChatPendingMention, TeamChatMentions } from "@models";

/**
 * The "@" autocomplete behind the team chat composer, held here rather than in each page so the
 * desktop widget and the mobile page behave identically.  It owns nothing to do with the view: the
 * component hands it the text and the caret, and gets back what the text should now be.
 *
 * The composer only ever holds what the user can read -- "@Scott Gines".  Each name they pick is
 * remembered as a pending mention and turned into "@[Scott Gines](42)" markup by applyMentions() on
 * the way to the server.
 */
export class TeamChatMentionAutocomplete {
	/** Longest run after an "@" still treated as somebody's name being typed. */
	private static readonly MAX_QUERY_LENGTH = 30;

	/** How many names the list shows at once. */
	private static readonly MAX_SUGGESTIONS = 8;

	isOpen: boolean = false;
	users: ITeamChatMemberModel[] = [];
	selectedIndex: number = 0;

	/** Names picked so far, waiting to be written into the body when the message is sent. */
	private pendingMentions: ITeamChatPendingMention[] = [];

	/** Where the "@" being completed sits in the text. */
	private startIndex: number = -1;

	/** The "@" the user pressed Esc on -- stays closed until they start a different one. */
	private dismissedIndex: number = -1;

	constructor(private getUsers: () => Promise<ITeamChatMemberModel[]>, private currentUserId: number) {
	}

	/** Called as the composer changes: opens, filters or closes the list to match what's been typed. */
	async update(text: string, caret: number): Promise<void> {
		const mentionQuery = this.getMentionQuery(text, caret);

		if (!mentionQuery) {
			this.close();
			return;
		}

		// Esc'd this one already -- leave it closed until they start another "@" somewhere else.
		if (mentionQuery.startIndex === this.dismissedIndex) {
			this.close();
			return;
		}

		this.dismissedIndex = -1;

		const query = mentionQuery.query.toLowerCase();
		const allUsers = (await this.getUsers()) || [];

		const matchingUsers = allUsers
			.filter(x => x.userId !== this.currentUserId)
			.filter(x => !query || (x.userFullName || "").toLowerCase().indexOf(query) >= 0)
			.slice(0, TeamChatMentionAutocomplete.MAX_SUGGESTIONS);

		// Nothing matching means they're just typing an "@" in a sentence, so get out of the way.
		if (matchingUsers.length === 0) {
			this.close();
			return;
		}

		this.users = matchingUsers;
		this.startIndex = mentionQuery.startIndex;
		this.selectedIndex = 0;
		this.isOpen = true;
	}

	moveSelection(offset: number) {
		if (!this.isOpen)
			return;

		const count = this.users.length;

		this.selectedIndex = ((this.selectedIndex + offset) % count + count) % count;
	}

	get selectedUser(): ITeamChatMemberModel {
		return this.isOpen ? this.users[this.selectedIndex] : null;
	}

	/**
	 * Drops the picked name in over whatever was typed after the "@", with a trailing space so the
	 * sentence carries straight on.  Returns the text the composer should now hold and where to put
	 * the caret.
	 */
	select(user: ITeamChatMemberModel, text: string, caret: number): { text: string; caret: number } {
		if (!user || this.startIndex < 0)
			return { text, caret };

		// Trailing space so the sentence carries straight on -- unless the text already continues with
		// one, which is what happens when the mention is dropped into the middle of a sentence.
		const textAfterCaret = text.substring(caret);
		const mentionText = `@${user.userFullName}` + (/^\s/.test(textAfterCaret) ? "" : " ");
		const newText = text.substring(0, this.startIndex) + mentionText + textAfterCaret;

		if (!this.pendingMentions.some(x => x.userId === user.userId))
			this.pendingMentions.push({ userId: user.userId, userFullName: user.userFullName });

		const newCaret = this.startIndex + mentionText.length;

		this.close();

		return { text: newText, caret: newCaret };
	}

	/** Esc: back to ordinary typing, and this "@" doesn't re-open the list. */
	dismiss() {
		this.dismissedIndex = this.startIndex;
		this.close();
	}

	close() {
		this.isOpen = false;
		this.users = [];
		this.selectedIndex = 0;
		this.startIndex = -1;
	}

	/** Rewrites the typed names as mention markup and starts a fresh message. */
	applyMentions(body: string): string {
		const returnBody = TeamChatMentions.applyMentions(body, this.pendingMentions);

		this.reset();

		return returnBody;
	}

	/** Leaving the conversation (or sending) -- nothing typed carries over. */
	reset() {
		this.pendingMentions = [];
		this.dismissedIndex = -1;
		this.close();
	}

	/**
	 * The "@name" being typed at the caret, if there is one.  An "@" mid-word is somebody typing an
	 * email address, not tagging a person, so it doesn't count.
	 */
	private getMentionQuery(text: string, caret: number): { startIndex: number; query: string } {
		const textBeforeCaret = (text || "").substring(0, caret);
		const atIndex = textBeforeCaret.lastIndexOf("@");

		if (atIndex < 0)
			return null;

		const characterBeforeAt = (atIndex === 0) ? " " : textBeforeCaret.charAt(atIndex - 1);
		if (/\s/.test(characterBeforeAt) === false)
			return null;

		const query = textBeforeCaret.substring(atIndex + 1);

		// A name doesn't run onto the next line, and past a certain length they've clearly moved on.
		if (query.length > TeamChatMentionAutocomplete.MAX_QUERY_LENGTH || query.indexOf("\n") >= 0)
			return null;

		return { startIndex: atIndex, query };
	}
}
